'use es6';

import { ColumnSort, SortOrders, SortTypes } from '../../schema/sort-records';
import { getValidColumnSorts } from './report-sort-utils';
import { DATASET_TABLE } from '../../schema/table-records';
import I18n from 'I18n';
import PropTypes from 'prop-types';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { FormLabel } from 'foundations-components/transitional/components/FormLabel';
import { Select } from 'foundations-components/transitional/components/Select';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { getColumnLabel } from '../../utils/column-utils';
import { useReportMeta } from '../../metadata/report-meta';
import { useUserInfo } from '../../metadata/user-info';
import { createMeasureSortDependency } from './sort-utils';
import { shouldFieldUseBackendLabelSort } from './label-sort-utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const VALUE_OPTION = '@@VALUE@@';
const DISPLAY_ORDER_OPTION = '@@DISPLAY_ORDER@@';
const LABEL_OPTION = '@@LABEL@@';
const TYPE_TO_OPTION_VALUE = {
  [SortTypes.VALUE]: VALUE_OPTION,
  [SortTypes.DISPLAY_ORDER]: DISPLAY_ORDER_OPTION,
  [SortTypes.LABEL]: LABEL_OPTION
};
const SnowflakeColumnSortEditor = ({
  report: initialReport,
  column,
  onChange
}) => {
  const {
    reportWithMeta,
    error: reportWithMetaError
  } = useReportMeta(initialReport);
  const {
    data: userInfo,
    error: userInfoError
  } = useUserInfo();
  if (reportWithMetaError) {
    console.error(reportWithMetaError);
    console.error('failed to render SnowflakeColumnSortEditor due to error above');
    return null;
  }
  if (userInfoError) {
    console.error(userInfoError);
    console.error('failed to render SnowflakeColumnSortEditor due to error above');
    return null;
  }
  if (!userInfo || !reportWithMeta) {
    return null;
  }
  const {
    report,
    reportMeta
  } = reportWithMeta;
  const {
    valid,
    preferred
  } = getValidColumnSorts(report, column, reportMeta);
  if (valid.isEmpty()) {
    return null;
  }
  const currentSort = column.sort;
  if (!currentSort) {
    onChange(preferred);
    return null;
  }
  const valueOption = {
    text: I18n.text('reporting-snowflake.sort-types.VALUE'),
    value: VALUE_OPTION
  };
  const displayOrderOption = {
    text: I18n.text('reporting-snowflake.sort-types.DISPLAY_ORDER'),
    value: DISPLAY_ORDER_OPTION
  };
  const hasLabelOption = valid.some(sort => sort.type === SortTypes.LABEL);
  const hasNewLabelOption = hasLabelOption && shouldFieldUseBackendLabelSort(column.field, reportMeta);
  const labelOption = {
    text: I18n.text('reporting-snowflake.sort-types.LABEL'),
    value: LABEL_OPTION,
    badge: hasNewLabelOption ? /*#__PURE__*/_jsx(Badge, {
      use: "new",
      children: I18n.text('reporting-snowflake.new')
    }) : undefined
  };
  const orderOptions = [{
    text: I18n.text('reporting-snowflake.sort-orders.ASCENDING'),
    value: SortOrders.ASCENDING
  }, {
    text: I18n.text('reporting-snowflake.sort-orders.DESCENDING'),
    value: SortOrders.DESCENDING
  }];
  const hasDisplayOrderOption = valid.some(sort => sort.type === SortTypes.DISPLAY_ORDER);
  const currentValue = currentSort.by || TYPE_TO_OPTION_VALUE[currentSort.type];
  const currentOrder = currentSort && currentSort.order;
  const baseOptions = [hasDisplayOrderOption && displayOrderOption, hasLabelOption && labelOption, valueOption].filter(option => option);
  const measureSorts = valid.filter(sort => sort.by != null).map(sort => sort.by).toOrderedSet().toList();
  const measureOptions = measureSorts.map(measureAlias => {
    const measure = report.columns.get(measureAlias);
    if (!measure) {
      return undefined;
    }
    const dependencyColumn = createMeasureSortDependency(column, measure);
    if (!dependencyColumn) {
      return undefined;
    }
    const {
      table,
      name
    } = dependencyColumn.field;
    const snowflakeTable = reportMeta.tables.get(table);
    const snowflakeProperty = reportMeta.properties.getIn([table, name]);
    const value = measureAlias;
    const maybeDatasetField = table === DATASET_TABLE && reportMeta.datasetDefinition.fields.get(name);
    const text = getColumnLabel(dependencyColumn, snowflakeProperty, snowflakeTable, maybeDatasetField, initialReport.expressions);
    return {
      text,
      value
    };
  }).filter(value => value).toArray();
  const sortOptions = [...baseOptions, ...measureOptions];
  const handleSortChange = nextValue => {
    if (nextValue === VALUE_OPTION) {
      const nextSort = ColumnSort({
        priority: 0,
        type: SortTypes.VALUE,
        order: currentOrder || SortOrders.ASCENDING
      });
      onChange(nextSort);
      return;
    }
    if (nextValue === DISPLAY_ORDER_OPTION) {
      const nextSort = ColumnSort({
        priority: 0,
        type: SortTypes.DISPLAY_ORDER,
        order: currentOrder || SortOrders.ASCENDING
      });
      onChange(nextSort);
      return;
    }
    if (nextValue === LABEL_OPTION) {
      const nextSort = ColumnSort({
        priority: 0,
        type: SortTypes.LABEL,
        order: currentOrder || SortOrders.ASCENDING
      });
      onChange(nextSort);
      return;
    }
    const nextSort = ColumnSort({
      priority: 0,
      type: SortTypes.VALUE,
      order: currentOrder || SortOrders.ASCENDING,
      by: nextValue
    });
    onChange(nextSort);
  };
  const handleSortOrderChange = nextOrder => {
    const nextSort = currentSort.set('order', nextOrder);
    onChange(nextSort);
  };
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsx(FormLabel, {
      children: I18n.text(`reporting-snowflake.column-options.sort`)
    }), /*#__PURE__*/_jsx(FormControl, {
      "aria-label": I18n.text('reporting-snowflake.sort-editor.select-column-label'),
      children: /*#__PURE__*/_jsx(Select, {
        onChange: ({
          target: {
            value: nextColumn
          }
        }) => {
          handleSortChange(nextColumn);
        },
        options: sortOptions,
        value: currentValue,
        placeholder: I18n.text('reporting-snowflake.sort-editor.select-column-placeholder')
      })
    }), /*#__PURE__*/_jsx(FormControl, {
      "aria-label": I18n.text('reporting-snowflake.sort-editor.select-order-label'),
      children: /*#__PURE__*/_jsx(Select, {
        onChange: ({
          target: {
            value: nextOrder
          }
        }) => {
          handleSortOrderChange(nextOrder);
        },
        options: orderOptions,
        value: currentOrder
      })
    })]
  });
};
SnowflakeColumnSortEditor.propTypes = {
  report: PropTypes.object.isRequired,
  column: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired
};
export default SnowflakeColumnSortEditor;